package one.healthium.base.preferences

import com.russhwolf.settings.Settings
import com.russhwolf.settings.set
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

class AppPreferences {
    private val settings = Settings()

    var isMetric
        get() = settings.getBoolean(KEY_IS_METRIC, false)
        set(value) {
            settings[KEY_IS_METRIC] = value
            _isMetricFlow.value = value
        }
    private val _isMetricFlow = MutableStateFlow(isMetric)
    val isMetricFlow = _isMetricFlow.asStateFlow()

    var mealPlanId
        get() = settings.getStringOrNull(KEY_MEAL_PLAN_ID)
        set(value) {
            settings[KEY_MEAL_PLAN_ID] = value
            _mealPlanIdFlow.value = value
        }
    private val _mealPlanIdFlow = MutableStateFlow(mealPlanId)
    val mealPlanIdFlow = _mealPlanIdFlow.asStateFlow()

    var dailyTaskDayIndex
        get() = settings.getInt(KEY_DAILY_TASKS_DAY_INDEX, 0)
        set(value) {
            settings[KEY_DAILY_TASKS_DAY_INDEX] = value
        }

    var dailyTaskUnread
        get() = settings.getBoolean(KEY_DAILY_TASKS_UNREAD, false)
        set(value) {
            settings[KEY_DAILY_TASKS_UNREAD] = value
            _dailyTaskUnreadFlow.value = value
        }
    private val _dailyTaskUnreadFlow = MutableStateFlow(dailyTaskUnread)
    val dailyTaskUnreadFlow = _dailyTaskUnreadFlow.asStateFlow()

    var lastVisitDate
        get() = settings.getLongOrNull(KEY_LAST_VISIT_DATE)
        set(value) {
            settings[KEY_LAST_VISIT_DATE] = value
        }
    var visitsInRow
        get() = settings.getInt(KEY_VISITS_IN_ROW, 0)
        set(value) {
            settings[KEY_VISITS_IN_ROW] = value
        }
    private val _visitsInRowFlow = MutableStateFlow(visitsInRow)
    val visitsInRowFlow = _visitsInRowFlow.asStateFlow()

    var latestFoodSuggestionsFile
        get() = settings.getStringOrNull(KEY_LATEST_FOOD_SUGGESTIONS_FILE)
        set(value) {
            settings[KEY_LATEST_FOOD_SUGGESTIONS_FILE] = value
        }

    var appVisitCounter
        get() = settings.getIntOrNull(KEY_APP_VISITS) ?: 0
        set(value) {
            settings[KEY_APP_VISITS] = value
        }
    var didRateApp
        get() = settings.getBooleanOrNull(KEY_DID_RATE_APP) ?: false
        set(value) {
            settings[KEY_DID_RATE_APP] = value
        }
    var foodLogCount
        get() = settings.getIntOrNull(KEY_FOOD_LOG_COUNT) ?: 0
        set(value) {
            settings[KEY_FOOD_LOG_COUNT] = value
        }
    var didTriggerWeightRatePopup
        get() = settings.getBooleanOrNull(KEY_DID_TRIGGER_WEIGHT_RATE_POPUP) ?: false
        set(value) {
            settings[KEY_DID_TRIGGER_WEIGHT_RATE_POPUP] = value
        }
    var didTriggerStartPopup
        get() = settings.getBooleanOrNull(KEY_DID_TRIGGER_START_POPUP) ?: false
        set(value) {
            settings[KEY_DID_TRIGGER_START_POPUP] = value
        }

    var didReactToHKBanner
        get() = settings.getBooleanOrNull(KEY_DID_REACT_TO_HK_BANNER)
        set(value) {
            settings[KEY_DID_REACT_TO_HK_BANNER] = value
        }

    var latestShownTipId
        get() = settings.getStringOrNull(KEY_LATEST_SHOWN_TIP_ID)
        set(value) {
            settings[KEY_LATEST_SHOWN_TIP_ID] = value
        }

    var latestShownTipTimestamp
        get() = settings.getLongOrNull(KEY_LATEST_SHOWN_TIP_TIMESTAMP)
        set(value) {
            settings[KEY_LATEST_SHOWN_TIP_TIMESTAMP] = value
        }

    var attRequested
        get() = settings.getBooleanOrNull(KEY_ATT_REQUESTED) ?: false
        set(value) {
            settings[KEY_ATT_REQUESTED] = value
        }

    var notificationPermissionRequested
        get() = settings.getBooleanOrNull(KEY_NOTIFICATION_PERMISSION_REQUESTED) ?: false
        set(value) {
            settings[KEY_NOTIFICATION_PERMISSION_REQUESTED] = value
        }
    var playTimeLastSessionStartTimestamp
        get() = settings.getLongOrNull(KEY_PLAY_TIME_LAST_SESSION_START_TIMESTAMP)
        set(value) {
            settings[KEY_PLAY_TIME_LAST_SESSION_START_TIMESTAMP] = value
        }

    var playTimeDuration
        get() = settings.getLongOrNull(KEY_PLAY_TIME_DURATION)
        set(value) {
            settings[KEY_PLAY_TIME_DURATION] = value
        }

    var introTutorialShown
        get() = settings.getBooleanOrNull(KEY_TUTORIAL_SHOWN) ?: false
        set(value) {
            settings[KEY_TUTORIAL_SHOWN] = value
        }

    var scheduledRemindersIds
        get() = settings.getStringOrNull(KEY_SCHEDULED_REMINDERS_IDS)?.split(':')?.mapNotNull { it.toIntOrNull() }.orEmpty()
        set(value) {
            settings[KEY_SCHEDULED_REMINDERS_IDS] = value.joinToString(":")
        }

    var periodTrackerEnabled
        get() = settings.getBoolean(KEY_PERIOD_TRACKER_ENABLED, true)
        set(value) {
            settings[KEY_PERIOD_TRACKER_ENABLED] = value
            _periodTrackerEnabledFlow.value = value
        }
    private val _periodTrackerEnabledFlow = MutableStateFlow(periodTrackerEnabled)
    val periodTrackerEnabledFlow = _periodTrackerEnabledFlow.asStateFlow()

    var remarketingActive
        get() = settings.getBoolean(KEY_REMARKETING_ACTIVE, false)
        set(value) {
            settings[KEY_REMARKETING_ACTIVE] = value
            _remarketingActiveFlow.value = value
        }
    private val _remarketingActiveFlow = MutableStateFlow(remarketingActive)
    val remarketingActiveFlow = _remarketingActiveFlow.asStateFlow()

    var onboardingPassed
        get() = settings.getBoolean(KEY_ONBOARDING_PASSED, false)
        set(value) {
            settings[KEY_ONBOARDING_PASSED] = value
        }

    var greetingText
        get() = settings.getStringOrNull(KEY_GREETING_TEXT)
        set(value) {
            settings[KEY_GREETING_TEXT] = value
        }

    var greetingTimestamp
        get() = settings.getLongOrNull(KEY_GREETING_TIMESTAMP)
        set(value) {
            settings[KEY_GREETING_TIMESTAMP] = value
        }

    var contentScrollSavedId
        get() = settings.getStringOrNull(KEY_CONTENT_SCROLL_SAVED_ID)
        set(value) {
            settings[KEY_CONTENT_SCROLL_SAVED_ID] = value
        }

    var contentSavedScrollFirstItemIndex
        get() = settings.getIntOrNull(KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_INDEX)
        set(value) {
            settings[KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_INDEX] = value
        }

    var contentSavedScrollFirstItemOffset
        get() = settings.getIntOrNull(KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_OFFSET)
        set(value) {
            settings[KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_OFFSET] = value
        }

    var latestReadInsightTimestamp
        get() = settings.getLongOrNull(KEY_LATEST_READ_INSIGHT_TIMESTAMP)
        set(value) {
            settings[KEY_LATEST_READ_INSIGHT_TIMESTAMP] = value
            _latestReadInsightTimestampFlow.value = value
        }
    private val _latestReadInsightTimestampFlow = MutableStateFlow(latestReadInsightTimestamp)
    val latestReadInsightTimestampFlow = _latestReadInsightTimestampFlow.asStateFlow()

    var latestReadMealPlanTimestamp
        get() = settings.getLongOrNull(KEY_LATEST_READ_MEAL_PLAN_TIMESTAMP)
        set(value) {
            settings[KEY_LATEST_READ_MEAL_PLAN_TIMESTAMP] = value
            _latestReadMealPlanTimestampFlow.value = value
        }
    private val _latestReadMealPlanTimestampFlow = MutableStateFlow(latestReadMealPlanTimestamp)
    val latestReadMealPlanTimestampFlow = _latestReadMealPlanTimestampFlow.asStateFlow()

    // when the user logs food from meal plan screen only
    var latestLogFoodTimestamp
        get() = settings.getLongOrNull(KEY_LATEST_LOG_FOOD_TIMESTAMP)
        set(value) {
            settings[KEY_LATEST_LOG_FOOD_TIMESTAMP] = value
            _latestLogFoodTimestampFlow.value = value
        }
    private val _latestLogFoodTimestampFlow = MutableStateFlow(latestLogFoodTimestamp)
    val latestLogFoodTimestampFlow = _latestLogFoodTimestampFlow.asStateFlow()

    var userPUID
        get() = settings.getStringOrNull(KEY_USER_PUID)
        set(value) {
            settings[KEY_USER_PUID] = value
        }

    var isFirstAppLaunch
        get() = settings.getBoolean(KEY_FIRST_APP_LAUNCH, true)
        set(value) {
            settings[KEY_FIRST_APP_LAUNCH] = value
        }

    var isMealPlanTutorialShown
        get() = settings.getBoolean(KEY_MEAL_PLAN_TUTORIAL_SHOWN, false)
        set(value) {
            settings[KEY_MEAL_PLAN_TUTORIAL_SHOWN] = value
        }

    var mealPlanSelectedDayIndex
        get() = settings.getInt(KEY_MEAL_PLAN_SELECTED_DAY_INDEX, 0)
        set(value) {
            settings[KEY_MEAL_PLAN_SELECTED_DAY_INDEX] = value
        }

    var lastSyncedLocale
        get() = settings.getStringOrNull(KEY_LAST_SYNCED_LOCALE)
        set(value) {
            settings[KEY_LAST_SYNCED_LOCALE] = value
        }

    fun clearUserPreferences() {
        // Clear all except food suggestions
        settings.remove(KEY_VISITS_IN_ROW)
        settings.remove(KEY_IS_METRIC)
        settings.remove(KEY_MEAL_PLAN_ID)
        settings.remove(KEY_CONTENT_SCROLL_SAVED_ID)
        settings.remove(KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_INDEX)
        settings.remove(KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_OFFSET)
        settings.remove(KEY_DAILY_TASKS_DAY_INDEX)
        settings.remove(KEY_DAILY_TASKS_UNREAD)
        settings.remove(KEY_LAST_VISIT_DATE)
        settings.remove(KEY_APP_VISITS)
        settings.remove(KEY_DID_RATE_APP)
        settings.remove(KEY_FOOD_LOG_COUNT)
        settings.remove(KEY_FIRST_WEIGHT_LOSS)
        settings.remove(KEY_DID_TRIGGER_WEIGHT_RATE_POPUP)
        settings.remove(KEY_DID_TRIGGER_START_POPUP)
        settings.remove(KEY_DID_REACT_TO_HK_BANNER)
        settings.remove(KEY_TUTORIAL_SHOWN)
        settings.remove(KEY_PLAY_TIME_LAST_SESSION_START_TIMESTAMP)
        settings.remove(KEY_PLAY_TIME_DURATION)
        settings.remove(KEY_REMARKETING_ACTIVE)
        settings.remove(KEY_ONBOARDING_PASSED)
        settings.remove(KEY_LATEST_READ_INSIGHT_TIMESTAMP)
        settings.remove(KEY_LATEST_READ_MEAL_PLAN_TIMESTAMP)
        settings.remove(KEY_LATEST_LOG_FOOD_TIMESTAMP)
        settings.remove(KEY_LAST_SYNCED_LOCALE)
    }

    companion object {
        private const val KEY_IS_METRIC = "is-metric"
        private const val KEY_MEAL_PLAN_ID = "meal-plan-id"
        private const val KEY_CONTENT_SCROLL_SAVED_ID = "content-scroll-saved-id"
        private const val KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_INDEX = "content-saved-scroll-first-item-index"
        private const val KEY_CONTENT_SAVED_SCROLL_FIRST_ITEM_OFFSET = "content-saved-scroll-first-item-offset"
        private const val KEY_DAILY_TASKS_DAY_INDEX = "daily-tasks-day-index"
        private const val KEY_DAILY_TASKS_UNREAD = "daily-tasks-unread"
        private const val KEY_LAST_VISIT_DATE = "last-visit-date"
        private const val KEY_VISITS_IN_ROW = "visits-in-row"
        private const val KEY_LATEST_FOOD_SUGGESTIONS_FILE = "latest-food-suggestions-file"
        private const val KEY_APP_VISITS = "app-visits"
        private const val KEY_FOOD_LOG_COUNT = "food-log-count"
        private const val KEY_FIRST_WEIGHT_LOSS = "first-weight-loss"
        private const val KEY_DID_RATE_APP = "did-rate-app"
        private const val KEY_DID_TRIGGER_WEIGHT_RATE_POPUP = "did-trigger-weight-rate-popup"
        private const val KEY_DID_TRIGGER_START_POPUP = "did-trigger-start-popup"
        private const val KEY_DID_REACT_TO_HK_BANNER = "did-react-to-hk-banner"
        private const val KEY_LATEST_SHOWN_TIP_ID = "latest-shown-tip-id"
        private const val KEY_LATEST_SHOWN_TIP_TIMESTAMP = "latest-shown-tip-timestamp"
        private const val KEY_ATT_REQUESTED = "ATT-requested"
        private const val KEY_NOTIFICATION_PERMISSION_REQUESTED = "notification-permission-requested"
        private const val KEY_TUTORIAL_SHOWN = "tutorial_shown"
        private const val KEY_PLAY_TIME_LAST_SESSION_START_TIMESTAMP = "play-time-last-session-start-timestamp"
        private const val KEY_PLAY_TIME_DURATION = "play-time-duration"
        private const val KEY_SCHEDULED_REMINDERS_IDS = "scheduled-reminders-ids"
        private const val KEY_PERIOD_TRACKER_ENABLED = "period-tracker-enabled"
        private const val KEY_REMARKETING_ACTIVE = "remarketing-active"
        private const val KEY_ONBOARDING_PASSED = "onboarding-passed"
        private const val KEY_GREETING_TEXT = "greeting-text"
        private const val KEY_GREETING_TIMESTAMP = "greeting-timestamp"
        private const val KEY_LATEST_READ_INSIGHT_TIMESTAMP = "latest-read-insight-timestamp"
        private const val KEY_LATEST_READ_MEAL_PLAN_TIMESTAMP = "latest-read-meal-plan-timestamp"
        private const val KEY_LATEST_LOG_FOOD_TIMESTAMP = "latest-log-food-timestamp"
        private const val KEY_USER_PUID = "user-puid"
        private const val KEY_FIRST_APP_LAUNCH = "first-app-launch"
        private const val KEY_MEAL_PLAN_TUTORIAL_SHOWN = "meal-plan-tutorial-shown"
        private const val KEY_MEAL_PLAN_SELECTED_DAY_INDEX = "meal-plan-selected-day-index"
        private const val KEY_LAST_SYNCED_LOCALE = "last-synced-locale"
    }
}